import { html, ref } from '@microsoft/fast-element';
import type { SellerRfqQuery } from './seller-rfq-query';
import { minusSignIcon, criteriaIcon } from '../../../../styles/icons';
import { TECHNOLOGY } from '../../../../utils';
import { sync } from '@genesislcap/foundation-utils';
import { eventDetail } from '../../../../utils/formatting';

export const SellerRfqQueryTemplate = html<SellerRfqQuery>`
  <div class="container">
    <div class="container-header">
      <h3 class="container-title my-projects-padding">RFQ Query</h3>
    </div>
    <div class="dialog-content">
      <form class="seller-rfq-query-form">
        <div class="form-control required">
          <label class="form-label">Technology Type</label>
          <zero-segmented-control
            appearance="primary"
            orientation="horizontal"
            :value="${sync((x) => x.techTypeValue)}"
            :disabled=${(x) => x.projectSelected}
            ${ref('technologyTypeElement')}
          >
            <zero-segmented-item
              class="custom-segmented-item"
              value="${TECHNOLOGY.WIND}"
              :current-checked=${(x) =>
                x.techTypeValue !== null && x.techTypeValue === TECHNOLOGY.WIND}
            >
              Wind
            </zero-segmented-item>
            <zero-segmented-item
              class="custom-segmented-item"
              value="${TECHNOLOGY.SOLAR}"
              :current-checked=${(x) =>
                x.techTypeValue !== null && x.techTypeValue === TECHNOLOGY.SOLAR}
            >
              Solar
            </zero-segmented-item>
          </zero-segmented-control>
        </div>

        <div class="form-control required">
          <label>ISO</label>
          <cascade-multi-select
            name="iso"
            ${ref('isoElement')}
            :isPositionRelative=${(x) => x.isPositionRelative}
            @registeredClickCallback="${(x, c) => x.isoSelectionChanged(eventDetail(c))}"
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label>Settlement Point</label>
          <cascade-multi-select
            name="settlementPoints"
            ${ref('settlementPointsElement')}
            :isPositionRelative=${(x) => x.isPositionRelative}
            :isDisabled=${sync((x) => x.disableByIsoSelected)}
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label class="form-label">Contract Type</label>
          <cascade-multi-select
            name="contractTypes"
            ${ref('contractTypeElement')}
            :isPositionRelative=${(x) => x.isPositionRelative}
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label>Applicable REC Program</label>
          <cascade-multi-select
            name="recElement"
            ${ref('recElement')}
            :isPositionRelative=${(x) => x.isPositionRelative}
          ></cascade-multi-select>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Price Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.priceMinValue)}
              ${ref('priceMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.priceMaxValue)}
              ${ref('priceMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Tenor Date Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter YYYY"
              min="2022"
              oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null"
              :value=${sync((x) => x.tenorDateMinValue)}
              ${ref('tenorDateMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter YYYY"
              min="2022"
              oninput="this.value = !!this.value && Math.abs(this.value) >= 0 ? Math.abs(this.value) : null"
              :value=${sync((x) => x.tenorDateMaxValue)}
              ${ref('tenorDateMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Volume Range (MWh/year)</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.volumeMinValue)}
              ${ref('volumeMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.volumeMaxValue)}
              ${ref('volumeMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Buyer Post-COD Credit ($/MW) Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.buyerPostCollateralMinValue)}
              ${ref('buyerPostCollateralMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.buyerPostCollateralMaxValue)}
              ${ref('buyerPostCollateralMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Expected Settlement Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.expectedSettlementMinValue)}
              ?disabled="${(x) => x.disableByRecSelected}"
              ${ref('expectedSettlementMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.expectedSettlementMaxValue)}
              ?disabled="${(x) => x.disableByRecSelected}"
              ${ref('expectedSettlementMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Downside Risk Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.downsideRiskMinValue)}
              ?disabled="${(x) => x.disableByRecSelected}"
              ${ref('downsideRiskMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.downsideRiskMaxValue)}
              ?disabled="${(x) => x.disableByRecSelected}"
              ${ref('downsideRiskMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Historical Nodal LME Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              max="1"
              oninput="this.value = !!this.value && this.value >= 0 && this.value <= 1? this.value : null"
              :value=${sync((x) => x.historicalNodalLMEMinValue)}
              ${ref('historicalNodalLMEMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              max="1"
              oninput="this.value = !!this.value && this.value >= 0 && this.value <= 1? this.value : null"
              :value=${sync((x) => x.historicalNodalLMEMaxValue)}
              ${ref('historicalNodalLMEMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control no-padding-bottom">
          <label class="form-label">Implied Carbon Price Range</label>
          <div class="range-value">
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Min..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.impliedCarbonPriceMinValue)}
              ${ref('impliedCarbonPriceMinValueElement')}
            ></zero-text-field>
            <div class="minus-sign">${minusSignIcon('')}</div>
            <zero-text-field
              class="input-control"
              type="number"
              placeholder="Enter Max..."
              min="0"
              oninput="this.value = !!this.value && this.value >= 0 ? this.value : null"
              :value=${sync((x) => x.impliedCarbonPriceMaxValue)}
              ${ref('impliedCarbonPriceMaxValueElement')}
            ></zero-text-field>
          </div>
        </div>

        <div class="form-control">
          <label>Negative Pricing Method</label>
          <cascade-multi-select
            name="negativePricingMethod"
            :isPositionRelative=${(x) => x.isPositionRelative}
            :isDisabled="${(x) => x.disableByRecSelected}"
            ${ref('negativePricingMethodElement')}
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label>Alternative Pricing Structures</label>
          <cascade-multi-select
            name="alternativePricingStructures"
            :isPositionRelative=${(x) => x.isPositionRelative}
            :isDisabled="${(x) => x.disableByRecSelected}"
            ${ref('alternativePricingStructuresElement')}
          ></cascade-multi-select>
        </div>

        <div class="form-control">
          <label>Performance Guarantee Method</label>
          <cascade-multi-select
            name="performanceGuaranteeMethod"
            :isPositionRelative=${(x) => x.isPositionRelative}
            :isDisabled="${(x) => x.disableByRecSelected}"
            ${ref('performanceGuaranteeMethodElement')}
          ></cascade-multi-select>
        </div>
      </form>
    </div>
    <div class="container-buttons">
      <zero-button appearance="secondary" @click=${(x) => x.clearFilters()}>Clear</zero-button>
      <zero-button
        appearance="primary"
        @click=${(x) => x.sendCriteria()}
        ?disabled=${sync((x) => x.disableByIsoSelected)}
      >
        ${criteriaIcon('start')} Filter RFQ's
      </zero-button>
    </div>
  </div>
`;
